<template>
  <v-container fluid class="admin-page voucher-admin pa-4 pa-md-7">
    <div class="page-heading mb-6">
      <div>
        <div class="eyebrow">PROMOTION OPERATIONS</div>
        <h1 class="page-title">Voucher Pages</h1>
        <p class="page-description mb-0">
          Create shareable registration links and review every voucher request.
        </p>
      </div>
      <v-btn dark color="#123f3a" class="text-capitalize px-5" @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        New voucher page
      </v-btn>
    </div>

    <Alert />

    <v-sheet class="workspace-panel mb-7" outlined>
      <div class="panel-toolbar">
        <div>
          <h2 class="section-title">Voucher sets</h2>
          <span class="section-caption">{{ events.length }} registration pages</span>
        </div>
        <div class="toolbar-actions">
          <v-text-field
            v-model="eventSearch"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="Search voucher pages"
            class="search-field"
            @keyup.enter="fetchEvents"
            @click:clear="fetchEvents"
          ></v-text-field>
          <v-btn icon :loading="loadingEvents" title="Refresh" @click="fetchEvents">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="eventHeaders"
        :items="events"
        :loading="loadingEvents"
        :items-per-page="10"
        item-key="id"
        class="voucher-table"
        :class="{ 'has-selection': selectedEventId }"
        @click:row="selectEvent"
      >
        <template v-slot:[`item.event_name`]="{ item }">
          <div class="event-cell py-2">
            <div class="font-weight-bold">{{ item.event_name }}</div>
            <div class="event-id">{{ item.id }}</div>
          </div>
        </template>

        <template v-slot:[`item.dates`]="{ item }">
          <div>{{ formatDate(item.date_start) }}</div>
          <div class="event-id">to {{ formatDate(item.date_end) }}</div>
        </template>

        <template v-slot:[`item.default_registration_status`]="{ item }">
          <v-chip
            small
            label
            :color="statusColor(item.default_registration_status)"
            :text-color="statusTextColor(item.default_registration_status)"
          >
            {{ statusLabel(item.default_registration_status) }} by default
          </v-chip>
        </template>

        <template v-slot:[`item.registration_summary`]="{ item }">
          <div class="summary-line">
            <span class="summary-total">{{ item.registration_summary.total }}</span>
            <span class="summary-dot accepted-dot"></span>{{ item.registration_summary.accepted }}
            <span class="summary-dot pending-dot"></span>{{ item.registration_summary.pending }}
            <span class="summary-dot cancelled-dot"></span>{{ item.registration_summary.cancelled }}
          </div>
        </template>

        <template v-slot:[`item.auto_issue`]="{ item }">
          <div>
            <v-icon small :color="item.auto_issue ? '#2d766e' : 'grey lighten-1'">
              {{ item.auto_issue ? "mdi-lightning-bolt" : "mdi-lightning-bolt-outline" }}
            </v-icon>
            <span class="text-caption ml-1">{{ item.auto_issue ? "On" : "Off" }}</span>
          </div>
          <div v-if="item.voucher_id" class="event-id">Set {{ item.voucher_id }}</div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end" @click.stop>
            <v-btn icon small title="Copy registration link" @click="copyLink(item)">
              <v-icon small>mdi-content-copy</v-icon>
            </v-btn>
            <v-btn icon small title="Open registration page" @click="openLink(item)">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn icon small title="Edit voucher page" @click="openEditDialog(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-sheet>

    <v-sheet class="workspace-panel" outlined>
      <div v-if="selectedEvent" class="registration-header">
        <div>
          <div class="eyebrow">REGISTRATION REVIEW</div>
          <h2 class="section-title mb-1">{{ selectedEvent.event_name }}</h2>
          <button class="share-link" type="button" @click="copyLink(selectedEvent)">
            {{ selectedEvent.registration_url }}
            <v-icon x-small class="ml-1">mdi-content-copy</v-icon>
          </button>
        </div>
        <div class="registration-controls">
          <div class="status-totals">
            <div v-for="metric in summaryMetrics" :key="metric.key" class="status-metric">
              <span class="metric-value">{{ registrationSummary[metric.key] }}</span>
              <span class="metric-label">{{ metric.label }}</span>
            </div>
          </div>
          <v-btn dark color="#1f776d" class="blast-button text-capitalize" @click="openBlastDialog">
            <v-icon left>mdi-whatsapp</v-icon>
            Blast vouchers
          </v-btn>
        </div>
      </div>

      <div v-if="selectedEvent" class="registration-filters">
        <v-text-field
          v-model="registrationSearch"
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="Search name, WhatsApp or code"
          class="search-field registration-search"
        ></v-text-field>
        <v-btn-toggle v-model="registrationStatusFilter" mandatory dense color="#123f3a">
          <v-btn v-for="filter in statusFilters" :key="filter.value" :value="filter.value" small>
            {{ filter.text }}
          </v-btn>
        </v-btn-toggle>
        <v-btn icon :loading="loadingRegistrations" title="Refresh" @click="fetchRegistrations">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-data-table
        v-if="selectedEvent"
        :headers="registrationHeaders"
        :items="filteredRegistrations"
        :loading="loadingRegistrations"
        :items-per-page="15"
        item-key="id"
        class="voucher-table registrations-table"
      >
        <template v-slot:[`item.full_name`]="{ item }">
          <div class="py-2">
            <div class="font-weight-bold">{{ item.full_name }}</div>
            <div class="event-id">Customer #{{ item.customer_id }}</div>
          </div>
        </template>

        <template v-slot:[`item.whatsapp_number`]="{ item }">
          <a v-if="item.whatsapp_number" :href="whatsappLink(item.whatsapp_number)" target="_blank">
            {{ item.whatsapp_number }}
          </a>
          <span v-else>—</span>
          <div v-if="item.voucher_sent_at" class="delivery-state sent-state">
            <v-icon x-small color="#2d766e">mdi-check-circle</v-icon>
            Sent {{ formatDateTime(item.voucher_sent_at) }}
          </div>
          <div v-else class="delivery-state unsent-state">
            <v-icon x-small color="#a4852c">mdi-clock-outline</v-icon>
            Voucher not sent
          </div>
          <div
            v-if="item.last_voucher_send_error"
            class="delivery-error"
            :title="item.last_voucher_send_error"
          >
            {{ item.last_voucher_send_error }}
          </div>
        </template>

        <template v-slot:[`item.family_details`]="{ item }">
          <div v-if="item.has_children">
            <span class="detail-label">Youngest child</span>
            <div>{{ item.youngest_child_age || "—" }}</div>
          </div>
          <div v-else>
            <span class="detail-label">Estimated delivery</span>
            <div>{{ formatMonth(item.estimated_delivery_month) }}</div>
          </div>
        </template>

        <template v-slot:[`item.voucher_code`]="{ item }">
          <div v-if="item.voucher_code" class="code-cell">
            {{ item.voucher_code }}
            <v-btn icon x-small title="Copy voucher code" @click="copyText(item.voucher_code, 'Voucher code copied')">
              <v-icon x-small>mdi-content-copy</v-icon>
            </v-btn>
          </div>
          <span v-else class="grey--text">Not issued</span>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-select
            :value="item.status"
            :items="registrationStatusOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="status-select"
            :background-color="statusColor(item.status)"
            :loading="Boolean(updatingStatus[item.id])"
            :disabled="Boolean(updatingStatus[item.id])"
            @change="updateStatus(item, $event)"
          ></v-select>
        </template>

        <template v-slot:[`item.send`]="{ item }">
          <v-btn
            small
            outlined
            class="text-capitalize send-one-button"
            :color="item.voucher_sent_at ? '#a4852c' : '#1f776d'"
            :loading="Boolean(sendingRegistration[item.id])"
            :disabled="Boolean(sendingRegistration[item.id]) || !item.whatsapp_number"
            :title="
              item.whatsapp_number
                ? item.voucher_sent_at
                  ? 'Resend the voucher QR message'
                  : 'Send the voucher QR message'
                : 'No WhatsApp number on file'
            "
            @click="openSendDialog(item)"
          >
            <v-icon left small>mdi-whatsapp</v-icon>
            {{ item.voucher_sent_at ? "Resend" : "Send" }}
          </v-btn>
        </template>
      </v-data-table>

      <div v-else class="empty-review">
        <v-icon size="38" color="#9badab">mdi-cursor-default-click-outline</v-icon>
        <h3>Select a voucher page</h3>
        <p>Its registrations and approval controls will appear here.</p>
      </div>
    </v-sheet>

    <v-dialog v-model="showFormDialog" max-width="760" scrollable>
      <v-card class="voucher-dialog">
        <v-card-title class="dialog-title">
          <div>
            <div class="eyebrow">{{ isEditing ? "EDIT PAGE" : "NEW VOUCHER PAGE" }}</div>
            <span>{{ isEditing ? form.event_name : "Create a registration link" }}</span>
          </div>
          <v-btn icon @click="closeFormDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="pt-5">
          <v-row>
            <v-col cols="12" md="7">
              <v-text-field
                v-model="form.event_name"
                label="Voucher page / event name"
                dense
                outlined
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-model="form.id"
                label="Event ID"
                dense
                outlined
                :disabled="isEditing"
                hint="Leave blank to generate a private 8-character ID"
                persistent-hint
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.date_start" type="date" label="Start date" dense outlined></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="form.date_end" type="date" label="End date" dense outlined></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.event_info"
                label="Event information shown on the registration page"
                rows="3"
                auto-grow
                dense
                outlined
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="form.default_registration_status"
                :items="defaultStatusOptions"
                item-text="text"
                item-value="value"
                label="New registration status"
                dense
                outlined
                hint="Applied automatically when a customer registers"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                v-model="form.auto_issue"
                color="#2d766e"
                inset
                label="Auto-issue voucher when accepted"
                class="mt-1"
              ></v-switch>
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="form.voucher_id"
                :items="voucherOptions"
                item-text="search_text"
                item-value="id"
                label="BonBon voucher"
                dense
                outlined
                clearable
                cache-items
                :loading="loadingVoucherSets"
                append-outer-icon="mdi-refresh"
                no-data-text="No BonBon vouchers found"
                hint="Used for both automatic issue and manual WhatsApp blasts."
                persistent-hint
                @click:append-outer="fetchVoucherSets"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content class="voucher-option">
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.unavailable ? item.id : voucherOptionDescription(item) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-chip v-if="!item.unavailable" x-small label :color="voucherAvailability(item).color">
                    {{ voucherAvailability(item).label }}
                  </v-chip>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="voucher-selection-text">
                    {{ item.name }}
                    <small v-if="!item.unavailable">· {{ formatVoucherValue(item) }}</small>
                  </span>
                </template>
              </v-autocomplete>

              <div
                v-if="selectedFormVoucher && !selectedFormVoucher.unavailable"
                class="voucher-specimen"
              >
                <div class="voucher-specimen-value">{{ formatVoucherValue(selectedFormVoucher) }}</div>
                <div class="voucher-specimen-copy">
                  <strong>{{ selectedFormVoucher.name }}</strong>
                  <span>
                    {{ applicabilityLabel(selectedFormVoucher.applicability) }} ·
                    {{ selectedFormVoucher.outlet_count }} outlet{{ selectedFormVoucher.outlet_count === 1 ? "" : "s" }}
                  </span>
                  <small>{{ selectedFormVoucher.id }}</small>
                </div>
              </div>
              <v-alert
                v-else-if="selectedFormVoucher && selectedFormVoucher.unavailable"
                dense
                text
                type="warning"
                class="mt-2"
              >
                This saved voucher ID is no longer returned by BonBon. Select another voucher before sending.
              </v-alert>
            </v-col>

            <v-col cols="12">
              <div class="field-label mb-1">Voucher QR artwork</div>
              <p class="field-caption mb-4">
                The text printed on the QR image sent over WhatsApp. Leave a field blank to keep the
                default shown underneath it.
              </p>

              <v-row>
                <v-col cols="12" md="7">
                  <v-text-field
                    v-model="form.qr_heading_text"
                    label="Heading"
                    dense
                    outlined
                    clearable
                    counter="120"
                    maxlength="120"
                    :placeholder="qrTextDefaults.heading"
                    persistent-placeholder
                    :hint="`Default: ${qrTextDefaults.heading}`"
                    persistent-hint
                  ></v-text-field>

                  <v-textarea
                    v-model="form.qr_terms_text"
                    label="Terms line"
                    rows="3"
                    auto-grow
                    dense
                    outlined
                    clearable
                    counter="200"
                    maxlength="200"
                    class="mt-5"
                    :placeholder="qrTextDefaults.terms"
                    persistent-placeholder
                    :hint="qrTermsHint"
                    persistent-hint
                  ></v-textarea>
                </v-col>

                <v-col cols="12" md="5">
                  <div class="qr-preview">
                    <div class="qr-preview-frame">
                      <v-progress-circular
                        v-if="qrPreviewLoading"
                        indeterminate
                        size="30"
                        width="2"
                        color="#1f776d"
                      ></v-progress-circular>
                      <img v-else-if="qrPreviewUrl" :src="qrPreviewUrl" alt="Example voucher QR" />
                      <div v-else class="qr-preview-empty">
                        <v-icon color="#9badab">mdi-qrcode</v-icon>
                        <span>{{ qrPreviewMessage }}</span>
                      </div>
                    </div>
                    <div class="qr-preview-caption">
                      Example only — the QR pixels are a placeholder, each registrant receives their
                      own code.
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-alert v-if="form.auto_issue && form.default_registration_status === 'pending'" text color="#826a28" icon="mdi-information-outline">
            Voucher codes will be created only after an admin changes a registration to Accepted.
          </v-alert>
          <v-alert v-else-if="form.auto_issue" text color="#2d766e" icon="mdi-lightning-bolt">
            New registrations will be accepted and issued immediately.
          </v-alert>
          <v-alert v-else-if="form.voucher_id" text color="#2d766e" icon="mdi-whatsapp">
            Automatic sending is off. Admin can issue and blast this voucher set manually.
          </v-alert>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn text class="text-capitalize" @click="closeFormDialog">Cancel</v-btn>
          <v-btn dark color="#123f3a" class="text-capitalize px-6" :loading="saving" @click="saveEvent">
            {{ isEditing ? "Save changes" : "Create & copy link" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSendDialog" max-width="520" persistent>
      <v-card class="voucher-dialog">
        <v-card-title class="dialog-title">
          <div>
            <div class="eyebrow">SEND VOUCHER QR</div>
            <span>{{ sendTarget ? sendTarget.full_name : "Send voucher" }}</span>
          </div>
          <v-btn icon :disabled="sendDialogLoading" @click="closeSendDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="sendTarget" class="pt-5">
          <v-alert
            v-if="sendTarget.voucher_sent_at"
            text
            type="warning"
            class="already-sent-warning mb-4"
            icon="mdi-alert-outline"
          >
            <div class="font-weight-bold">This registrant already received a voucher</div>
            <div class="text-caption">
              Sent {{ formatDateTime(sendTarget.voucher_sent_at) }}. Sending again delivers a second
              WhatsApp message with the same voucher code.
            </div>
          </v-alert>

          <v-alert
            v-if="sendTarget.last_voucher_send_error"
            text
            type="error"
            dense
            class="mb-4"
            icon="mdi-alert-circle-outline"
          >
            Last attempt failed: {{ sendTarget.last_voucher_send_error }}
          </v-alert>

          <div class="send-summary">
            <div class="send-summary-row">
              <span class="detail-label">WhatsApp</span>
              <span>{{ sendTarget.whatsapp_number || "No number on file" }}</span>
            </div>
            <div class="send-summary-row">
              <span class="detail-label">Status</span>
              <span>{{ statusLabel(sendTarget.status) }}</span>
            </div>
            <div class="send-summary-row">
              <span class="detail-label">Voucher code</span>
              <span>{{ sendTarget.voucher_code || "Issued on send" }}</span>
            </div>
            <div class="send-summary-row">
              <span class="detail-label">Voucher set</span>
              <span>
                {{ selectedEventVoucher ? selectedEventVoucher.name : selectedEvent && selectedEvent.voucher_id }}
              </span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn text class="text-capitalize" :disabled="sendDialogLoading" @click="closeSendDialog">
            Cancel
          </v-btn>
          <v-btn
            dark
            :color="sendTarget && sendTarget.voucher_sent_at ? '#a4852c' : '#1f776d'"
            class="text-capitalize px-6"
            :loading="sendDialogLoading"
            @click="confirmSendVoucher"
          >
            <v-icon left>mdi-send</v-icon>
            {{ sendTarget && sendTarget.voucher_sent_at ? "Send again" : "Send voucher" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBlastDialog" max-width="700" persistent scrollable>
      <v-card class="voucher-dialog blast-dialog">
        <v-card-title class="dialog-title">
          <div>
            <div class="eyebrow">WHATSAPP VOUCHER BLAST</div>
            <span>{{ selectedEvent ? selectedEvent.event_name : "Voucher blast" }}</span>
          </div>
          <v-btn icon :disabled="blastLoading" @click="closeBlastDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-5">
          <div class="template-strip mb-5">
            <div class="template-icon"><v-icon color="white">mdi-qrcode</v-icon></div>
            <div>
              <div class="font-weight-bold">
                {{ selectedEventVoucher ? selectedEventVoucher.name : "Voucher artwork + WhatsApp template" }}
              </div>
              <div class="text-caption">
                <template v-if="selectedEventVoucher">
                  {{ formatVoucherValue(selectedEventVoucher) }} ·
                  {{ applicabilityLabel(selectedEventVoucher.applicability) }}
                </template>
                <template v-else>Uses the existing dynamic QR design</template>
                and <strong>voucher1</strong> in English.
              </div>
              <div class="template-id">BonBon set {{ selectedEvent && selectedEvent.voucher_id }}</div>
            </div>
          </div>

          <div class="field-label mb-2">Choose recipients</div>
          <v-radio-group v-model="blastForm.audience" class="audience-group mt-0" hide-details>
            <v-radio
              v-for="option in blastAudienceOptions"
              :key="option.value"
              :value="option.value"
              color="#1f776d"
              class="audience-option"
            >
              <template v-slot:label>
                <div>
                  <div class="audience-title">{{ option.text }}</div>
                  <div class="audience-description">{{ option.description }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <v-divider class="my-5"></v-divider>

          <v-checkbox
            v-model="blastForm.resend"
            color="#1f776d"
            class="resend-checkbox mt-0"
            hide-details
          >
            <template v-slot:label>
              <div>
                <div class="audience-title">Resend to everyone in this audience</div>
                <div class="audience-description">
                  Off by default. When off, numbers with a successful voucher send are skipped.
                </div>
              </div>
            </template>
          </v-checkbox>

          <div v-if="!blastResult" class="dispatch-preview mt-5">
            <v-progress-circular
              v-if="blastPreviewLoading"
              indeterminate
              size="26"
              width="2"
              color="#1f776d"
            ></v-progress-circular>
            <template v-else>
              <div class="dispatch-count">{{ blastPreview.recipient_count }}</div>
              <div>
                <div class="font-weight-bold">WhatsApp messages ready</div>
                <div class="text-caption">
                  {{ blastPreview.previously_sent_skipped }} previously sent skipped
                </div>
              </div>
            </template>
          </div>

          <v-alert
            v-if="blastResult"
            text
            :type="blastResult.failed.length ? 'warning' : 'success'"
            class="mt-5 mb-3"
          >
            {{ blastResult.sent.length }} sent,
            {{ blastResult.failed.length }} failed,
            {{ blastResult.skipped.length }} skipped.
          </v-alert>

          <div v-if="blastResult && blastResult.failed.length" class="blast-failures">
            <div v-for="failure in blastResult.failed" :key="failure.id" class="failure-row">
              <strong>{{ failure.full_name }}</strong>
              <span>{{ failure.whatsapp_number || "No WhatsApp number" }}</span>
              <small>{{ failure.message }}</small>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn text class="text-capitalize" :disabled="blastLoading" @click="closeBlastDialog">
            {{ blastResult ? "Close" : "Cancel" }}
          </v-btn>
          <v-btn
            v-if="!blastResult"
            dark
            color="#1f776d"
            class="text-capitalize px-6"
            :loading="blastLoading"
            :disabled="blastPreviewLoading || !blastPreview.recipient_count"
            @click="sendBlast"
          >
            <v-icon left>mdi-send</v-icon>
            Send {{ blastPreview.recipient_count }} vouchers
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Alert from "@/components/Alert.vue";
import server from "../api";

const today = () => new Date().toISOString().slice(0, 10);
const defaultForm = () => ({
  id: "",
  event_name: "",
  date_start: today(),
  date_end: today(),
  event_info: "",
  default_registration_status: "pending",
  auto_issue: false,
  voucher_id: "",
  qr_heading_text: "",
  qr_terms_text: "",
});

const QR_TEXT_FALLBACKS = {
  heading: "Voucher Discount",
  terms: "* Terms and conditions apply. Not applicable to all products. Please chat for more details.",
};

export default {
  name: "VoucherManagement",
  components: { Alert },
  data() {
    return {
      events: [],
      registrations: [],
      voucherSets: [],
      selectedEventId: null,
      eventSearch: "",
      registrationSearch: "",
      registrationStatusFilter: "",
      registrationSummary: { total: 0, accepted: 0, pending: 0, cancelled: 0 },
      loadingEvents: false,
      loadingRegistrations: false,
      loadingVoucherSets: false,
      saving: false,
      blastLoading: false,
      blastPreviewLoading: false,
      showFormDialog: false,
      showBlastDialog: false,
      qrPreviewUrl: "",
      qrPreviewLoading: false,
      qrPreviewError: "",
      qrPreviewTimer: null,
      qrPreviewToken: 0,
      editingId: null,
      updatingStatus: {},
      sendingRegistration: {},
      showSendDialog: false,
      sendDialogLoading: false,
      sendTargetId: null,
      form: defaultForm(),
      blastForm: { audience: "accepted", resend: false },
      blastPreview: { recipient_count: 0, previously_sent_skipped: 0 },
      blastResult: null,
      eventHeaders: [
        { text: "Voucher page", value: "event_name" },
        { text: "Event dates", value: "dates", sortable: false },
        { text: "New entries", value: "default_registration_status", sortable: false },
        { text: "Registrations", value: "registration_summary", sortable: false },
        { text: "Auto issue", value: "auto_issue" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      registrationHeaders: [
        { text: "Customer", value: "full_name" },
        { text: "WhatsApp", value: "whatsapp_number" },
        { text: "Details", value: "family_details", sortable: false },
        { text: "Voucher code", value: "voucher_code" },
        { text: "Submitted", value: "createdAt" },
        { text: "Status", value: "status", sortable: false, width: 165 },
        { text: "Voucher QR", value: "send", sortable: false, align: "end", width: 130 },
      ],
      defaultStatusOptions: [
        { text: "Pending — review first", value: "pending" },
        { text: "Accepted — approve immediately", value: "accepted" },
      ],
      registrationStatusOptions: [
        { text: "Accepted", value: "accepted" },
        { text: "Pending", value: "pending" },
        { text: "Cancelled", value: "cancelled" },
      ],
      statusFilters: [
        { text: "All", value: "" },
        { text: "Accepted", value: "accepted" },
        { text: "Pending", value: "pending" },
        { text: "Cancelled", value: "cancelled" },
      ],
      summaryMetrics: [
        { label: "Total", key: "total" },
        { label: "Accepted", key: "accepted" },
        { label: "Pending", key: "pending" },
        { label: "Cancelled", key: "cancelled" },
      ],
      blastAudienceOptions: [
        {
          text: "All registrations",
          value: "all",
          description: "Accepted, pending and cancelled registrations.",
        },
        {
          text: "All accepted",
          value: "accepted",
          description: "Only registrations approved by an admin.",
        },
        {
          text: "All accepted & pending",
          value: "accepted_pending",
          description: "Exclude cancelled registrations.",
        },
      ],
    };
  },
  computed: {
    selectedEvent() {
      return this.events.find((event) => event.id === this.selectedEventId) || null;
    },
    isEditing() {
      return Boolean(this.editingId);
    },
    voucherOptions() {
      const options = this.voucherSets.map((voucherSet) => ({
        ...voucherSet,
        search_text: [
          voucherSet.name,
          voucherSet.id,
          this.formatVoucherValue(voucherSet),
          (voucherSet.outlets || []).map((outlet) => outlet.name).join(" "),
        ]
          .filter(Boolean)
          .join(" "),
      }));
      const selectedId = String(this.form.voucher_id || "").trim();
      if (selectedId && !options.some((voucherSet) => voucherSet.id === selectedId)) {
        options.unshift({
          id: selectedId,
          name: "Unavailable voucher set",
          search_text: selectedId,
          unavailable: true,
        });
      }
      return options;
    },
    selectedFormVoucher() {
      return this.voucherOptions.find((voucherSet) => voucherSet.id === this.form.voucher_id) || null;
    },
    // The API returns each voucher set's generated artwork text, so the dialog
    // shows the very same default the renderer would fall back to.
    qrTextDefaults() {
      const voucherSet = this.selectedFormVoucher;
      if (!voucherSet || voucherSet.unavailable || !voucherSet.qr_defaults) {
        return QR_TEXT_FALLBACKS;
      }
      return voucherSet.qr_defaults;
    },
    qrTermsHint() {
      const terms = String(this.form.qr_terms_text || "").trim();
      const length = terms.length || this.qrTextDefaults.terms.length;
      return length <= 30
        ? "Short lines are printed large and italic under the voucher value."
        : "Longer lines are printed small, wrapped over up to three lines.";
    },
    qrPreviewMessage() {
      if (this.qrPreviewError) return this.qrPreviewError;
      return "Select a BonBon voucher to preview the artwork";
    },
    selectedEventVoucher() {
      if (!this.selectedEvent) return null;
      return this.voucherSets.find((voucherSet) => voucherSet.id === this.selectedEvent.voucher_id) || null;
    },
    sendTarget() {
      if (!this.sendTargetId) return null;
      return this.registrations.find((registration) => registration.id === this.sendTargetId) || null;
    },
    filteredRegistrations() {
      const query = String(this.registrationSearch || "").trim().toLowerCase();
      if (!query) return this.registrations;
      return this.registrations.filter((registration) =>
        [registration.full_name, registration.whatsapp_number, registration.voucher_code]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
    },
  },
  watch: {
    registrationStatusFilter() {
      if (this.selectedEventId) this.fetchRegistrations();
    },
    "form.voucher_id"() {
      this.scheduleQrPreview();
    },
    "form.qr_heading_text"() {
      this.scheduleQrPreview();
    },
    "form.qr_terms_text"() {
      this.scheduleQrPreview();
    },
    "blastForm.audience"() {
      if (this.showBlastDialog && !this.blastResult) this.previewBlast();
    },
    "blastForm.resend"() {
      if (this.showBlastDialog && !this.blastResult) this.previewBlast();
    },
  },
  methods: {
    getAuthConfig(extra = {}) {
      return {
        ...extra,
        headers: { token: localStorage.token, ...(extra.headers || {}) },
      };
    },
    getErrorMessage(error, fallback) {
      const data = error.response && error.response.data;
      if (data && data.message) return data.message;
      if (data && Array.isArray(data.errors) && data.errors[0]) return data.errors[0].message;
      if (data && data.errors) return String(data.errors);
      return fallback;
    },
    statusLabel(status) {
      return { accepted: "Accepted", pending: "Pending", cancelled: "Cancelled" }[status] || status;
    },
    statusColor(status) {
      return { accepted: "#dcefe8", pending: "#fff1c9", cancelled: "#f3dfe0" }[status] || "#eeeeee";
    },
    statusTextColor(status) {
      return { accepted: "#1d6158", pending: "#785e19", cancelled: "#8c3c42" }[status] || "#333333";
    },
    formatDate(value) {
      if (!value) return "—";
      const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      return Number.isNaN(parsed.getTime())
        ? "—"
        : parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    formatDateTime(value) {
      if (!value) return "—";
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? "—"
        : parsed.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    },
    formatMonth(value) {
      if (!value) return "—";
      const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
      return Number.isNaN(parsed.getTime())
        ? "—"
        : parsed.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
    },
    whatsappLink(number) {
      return `https://wa.me/${String(number || "").replace(/\D/g, "")}`;
    },
    formatVoucherValue(voucherSet) {
      const amount = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(
        Number(voucherSet.amount || 0)
      );
      return voucherSet.type === "percentage" ? `${amount}%` : `Rp${amount}`;
    },
    applicabilityLabel(applicability) {
      return {
        all_available: "All products",
        manual_category: "Selected categories",
        manual_item: "Selected products",
      }[applicability] || applicability || "Unknown applicability";
    },
    voucherAvailability(voucherSet) {
      const now = Date.now();
      const start = voucherSet.started_at ? new Date(voucherSet.started_at).getTime() : null;
      const end = voucherSet.ended_at ? new Date(voucherSet.ended_at).getTime() : null;
      if (end && end < now) return { label: "Ended", color: "#f3dfe0" };
      if (start && start > now) return { label: "Upcoming", color: "#fff1c9" };
      return { label: "Available", color: "#dcefe8" };
    },
    voucherOptionDescription(voucherSet) {
      const outlets = (voucherSet.outlets || []).map((outlet) => outlet.name).join(", ") || "No outlet";
      return `${this.formatVoucherValue(voucherSet)} · ${this.applicabilityLabel(
        voucherSet.applicability
      )} · ${outlets}`;
    },
    fetchVoucherSets() {
      this.loadingVoucherSets = true;
      return server
        .get("/voucher-registration/admin/voucher-sets", this.getAuthConfig())
        .then((response) => {
          this.voucherSets = response.data.voucher_sets || [];
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to load BonBon vouchers"));
        })
        .finally(() => {
          this.loadingVoucherSets = false;
        });
    },
    fetchEvents() {
      this.loadingEvents = true;
      const params = {};
      if (this.eventSearch) params.search = this.eventSearch;
      return server
        .get("/voucher-registration/admin/events", this.getAuthConfig({ params }))
        .then((response) => {
          this.events = response.data.events || [];
          if (this.selectedEventId && !this.events.some((event) => event.id === this.selectedEventId)) {
            this.selectedEventId = null;
            this.registrations = [];
          }
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to load voucher pages"));
        })
        .finally(() => {
          this.loadingEvents = false;
        });
    },
    selectEvent(event) {
      this.selectedEventId = event.id;
      this.registrationStatusFilter = "";
      this.registrationSearch = "";
      this.fetchRegistrations();
    },
    fetchRegistrations() {
      if (!this.selectedEventId) return Promise.resolve();
      this.loadingRegistrations = true;
      const params = {};
      if (this.registrationStatusFilter) params.status = this.registrationStatusFilter;
      return server
        .get(
          `/voucher-registration/admin/events/${encodeURIComponent(this.selectedEventId)}/registrations`,
          this.getAuthConfig({ params })
        )
        .then((response) => {
          this.registrations = response.data.registrations || [];
          this.registrationSummary = response.data.summary || this.registrationSummary;
          const index = this.events.findIndex((event) => event.id === this.selectedEventId);
          if (index !== -1 && response.data.event) this.$set(this.events, index, response.data.event);
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to load registrations"));
        })
        .finally(() => {
          this.loadingRegistrations = false;
        });
    },
    // The preview is an authenticated PNG, so it is fetched as a blob rather
    // than pointed at from <img src>, and typing is debounced to one render.
    scheduleQrPreview() {
      if (!this.showFormDialog) return;
      clearTimeout(this.qrPreviewTimer);
      this.qrPreviewTimer = setTimeout(() => this.fetchQrPreview(), 400);
    },
    releaseQrPreview() {
      if (this.qrPreviewUrl) URL.revokeObjectURL(this.qrPreviewUrl);
      this.qrPreviewUrl = "";
    },
    fetchQrPreview() {
      const voucherId = String(this.form.voucher_id || "").trim();
      if (voucherId.length !== 26) {
        this.releaseQrPreview();
        this.qrPreviewLoading = false;
        this.qrPreviewError = "";
        return Promise.resolve();
      }

      // Only the newest request may paint, so a slow render cannot overwrite
      // the preview of text the admin has since changed.
      const token = ++this.qrPreviewToken;
      this.qrPreviewLoading = true;
      this.qrPreviewError = "";
      return server
        .get(
          "/voucher-registration/admin/qr-preview",
          this.getAuthConfig({
            params: {
              voucher_id: voucherId,
              heading: String(this.form.qr_heading_text || "").trim() || undefined,
              terms: String(this.form.qr_terms_text || "").trim() || undefined,
            },
            responseType: "blob",
          })
        )
        .then((response) => {
          if (token !== this.qrPreviewToken) return;
          this.releaseQrPreview();
          this.qrPreviewUrl = URL.createObjectURL(response.data);
        })
        .catch(() => {
          if (token !== this.qrPreviewToken) return;
          this.releaseQrPreview();
          this.qrPreviewError = "Preview unavailable for this voucher";
        })
        .finally(() => {
          if (token === this.qrPreviewToken) this.qrPreviewLoading = false;
        });
    },
    openCreateDialog() {
      this.editingId = null;
      this.form = defaultForm();
      this.releaseQrPreview();
      this.qrPreviewError = "";
      this.showFormDialog = true;
      if (!this.voucherSets.length) this.fetchVoucherSets();
    },
    openEditDialog(event) {
      this.editingId = event.id;
      this.form = {
        id: event.id,
        event_name: event.event_name || "",
        date_start: String(event.date_start || "").slice(0, 10),
        date_end: String(event.date_end || "").slice(0, 10),
        event_info: event.event_info || "",
        default_registration_status: event.default_registration_status || "pending",
        auto_issue: Boolean(event.auto_issue),
        voucher_id: event.voucher_id || "",
        qr_heading_text: event.qr_heading_text || "",
        qr_terms_text: event.qr_terms_text || "",
      };
      this.showFormDialog = true;
      if (!this.voucherSets.length) this.fetchVoucherSets();
      this.scheduleQrPreview();
    },
    closeFormDialog() {
      this.showFormDialog = false;
      this.editingId = null;
      this.form = defaultForm();
      clearTimeout(this.qrPreviewTimer);
      this.releaseQrPreview();
      this.qrPreviewError = "";
    },
    saveEvent() {
      if (!String(this.form.event_name || "").trim()) {
        this.$store.commit("SET_ERROR", "Voucher page name is required");
        return;
      }
      if (!this.form.date_start || !this.form.date_end) {
        this.$store.commit("SET_ERROR", "Start and end dates are required");
        return;
      }
      const voucherId = String(this.form.voucher_id || "").trim();
      if (voucherId && voucherId.length !== 26) {
        this.$store.commit("SET_ERROR", "BonBon voucher set ID must be 26 characters");
        return;
      }
      if (this.form.auto_issue && !voucherId) {
        this.$store.commit("SET_ERROR", "BonBon voucher set ID is required for auto-issue");
        return;
      }

      const payload = {
        event_name: this.form.event_name.trim(),
        date_start: this.form.date_start,
        date_end: this.form.date_end,
        event_info: this.form.event_info.trim(),
        default_registration_status: this.form.default_registration_status,
        auto_issue: this.form.auto_issue,
        voucher_id: voucherId || null,
        qr_heading_text: String(this.form.qr_heading_text || "").trim() || null,
        qr_terms_text: String(this.form.qr_terms_text || "").trim() || null,
      };
      this.saving = true;
      const request = this.isEditing
        ? server.put(
            `/voucher-registration/admin/events/${encodeURIComponent(this.editingId)}`,
            payload,
            this.getAuthConfig()
          )
        : server.post(
            "/voucher-registration/admin/events",
            { ...payload, id: this.form.id.trim() || undefined },
            this.getAuthConfig()
          );

      request
        .then(async (response) => {
          const event = response.data.event;
          this.$store.commit("SET_SUCCESS", this.isEditing ? "Voucher page updated" : "Voucher page created; link copied");
          if (!this.isEditing && event) await this.copyText(event.registration_url, null);
          this.closeFormDialog();
          await this.fetchEvents();
          if (event) {
            this.selectedEventId = event.id;
            await this.fetchRegistrations();
          }
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to save voucher page"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    updateStatus(registration, status) {
      if (!status || status === registration.status) return;
      this.$set(this.updatingStatus, registration.id, true);
      server
        .patch(
          `/voucher-registration/admin/registrations/${registration.id}/status`,
          { status },
          this.getAuthConfig()
        )
        .then((response) => {
          const sent = response.data.voucher_sent;
          this.$store.commit(
            "SET_SUCCESS",
            sent ? "Registration accepted and voucher sent" : `Registration marked ${this.statusLabel(status).toLowerCase()}`
          );
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to update registration status"));
        })
        .finally(() => {
          this.$delete(this.updatingStatus, registration.id);
          this.fetchRegistrations();
          this.fetchEvents();
        });
    },
    openSendDialog(registration) {
      if (!this.selectedEvent) return;
      if (!this.selectedEvent.voucher_id) {
        this.$store.commit("SET_ERROR", "Assign a BonBon voucher set ID before sending vouchers");
        this.openEditDialog(this.selectedEvent);
        return;
      }
      if (!registration.whatsapp_number) {
        this.$store.commit("SET_ERROR", `${registration.full_name} has no WhatsApp number on file`);
        return;
      }
      this.sendTargetId = registration.id;
      this.showSendDialog = true;
    },
    closeSendDialog() {
      if (this.sendDialogLoading) return;
      this.showSendDialog = false;
      this.sendTargetId = null;
    },
    // Sends one voucher QR message; resend is passed explicitly so the API only
    // ever delivers a duplicate after the admin confirmed the warning.
    confirmSendVoucher() {
      const registration = this.sendTarget;
      if (!registration) return;
      const resend = Boolean(registration.voucher_sent_at);

      this.sendDialogLoading = true;
      this.$set(this.sendingRegistration, registration.id, true);
      server
        .post(
          `/voucher-registration/admin/registrations/${registration.id}/send`,
          { resend },
          this.getAuthConfig()
        )
        .then((response) => {
          this.$store.commit(
            "SET_SUCCESS",
            response.data.message || `Voucher sent to ${registration.full_name}`
          );
          this.showSendDialog = false;
          this.sendTargetId = null;
        })
        // The dialog stays open on failure so the refreshed delivery error is visible.
        .catch((error) => {
          this.$store.commit(
            "SET_ERROR",
            this.getErrorMessage(error, `Failed to send voucher to ${registration.full_name}`)
          );
        })
        .finally(() => {
          this.sendDialogLoading = false;
          this.$delete(this.sendingRegistration, registration.id);
          this.fetchRegistrations();
          this.fetchEvents();
        });
    },
    openBlastDialog() {
      if (!this.selectedEvent) return;
      if (!this.selectedEvent.voucher_id) {
        this.$store.commit("SET_ERROR", "Assign a BonBon voucher set ID before blasting vouchers");
        this.openEditDialog(this.selectedEvent);
        return;
      }
      this.blastForm = { audience: "accepted", resend: false };
      this.blastPreview = { recipient_count: 0, previously_sent_skipped: 0 };
      this.blastResult = null;
      this.showBlastDialog = true;
      this.previewBlast();
    },
    closeBlastDialog() {
      if (this.blastLoading) return;
      this.showBlastDialog = false;
      this.blastResult = null;
    },
    previewBlast() {
      if (!this.selectedEventId || !this.showBlastDialog) return Promise.resolve();
      this.blastPreviewLoading = true;
      return server
        .post(
          `/voucher-registration/admin/events/${encodeURIComponent(this.selectedEventId)}/broadcast`,
          { ...this.blastForm, dry_run: true },
          this.getAuthConfig()
        )
        .then((response) => {
          this.blastPreview = {
            recipient_count: response.data.recipient_count || 0,
            previously_sent_skipped: response.data.previously_sent_skipped || 0,
          };
        })
        .catch((error) => {
          this.blastPreview = { recipient_count: 0, previously_sent_skipped: 0 };
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Failed to preview voucher blast"));
        })
        .finally(() => {
          this.blastPreviewLoading = false;
        });
    },
    sendBlast() {
      if (!this.selectedEventId || !this.blastPreview.recipient_count) return;
      this.blastLoading = true;
      server
        .post(
          `/voucher-registration/admin/events/${encodeURIComponent(this.selectedEventId)}/broadcast`,
          { ...this.blastForm, dry_run: false },
          this.getAuthConfig()
        )
        .then((response) => {
          this.blastResult = {
            sent: response.data.sent || [],
            failed: response.data.failed || [],
            skipped: response.data.skipped || [],
          };
          this.$store.commit("SET_SUCCESS", response.data.message || "Voucher blast finished");
          this.fetchRegistrations();
          this.fetchEvents();
        })
        .catch((error) => {
          this.$store.commit("SET_ERROR", this.getErrorMessage(error, "Voucher blast failed"));
        })
        .finally(() => {
          this.blastLoading = false;
        });
    },
    openLink(event) {
      window.open(event.registration_url, "_blank", "noopener");
    },
    copyLink(event) {
      return this.copyText(event.registration_url, "Registration link copied");
    },
    copyText(value, successMessage) {
      const text = String(value || "");
      const copy = navigator.clipboard && window.isSecureContext
        ? navigator.clipboard.writeText(text)
        : new Promise((resolve, reject) => {
            const input = document.createElement("textarea");
            input.value = text;
            input.style.position = "fixed";
            input.style.opacity = "0";
            document.body.appendChild(input);
            input.select();
            try {
              document.execCommand("copy") ? resolve() : reject(new Error("Copy failed"));
            } catch (error) {
              reject(error);
            } finally {
              document.body.removeChild(input);
            }
          });
      return copy
        .then(() => {
          if (successMessage) this.$store.commit("SET_SUCCESS", successMessage);
        })
        .catch(() => this.$store.commit("SET_ERROR", "Unable to copy; please copy the link manually"));
    },
  },
  created() {
    this.fetchEvents();
    this.fetchVoucherSets();
  },
  beforeDestroy() {
    clearTimeout(this.qrPreviewTimer);
    this.releaseQrPreview();
  },
};
</script>

<style scoped>
.voucher-admin {
  max-width: 1600px;
  color: #19312f;
}

.page-heading,
.panel-toolbar,
.registration-header,
.registration-filters,
.dialog-title,
.dialog-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.eyebrow {
  color: #b17850;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.page-title {
  margin: 2px 0 4px;
  color: #123f3a;
  font-family: "Averta", "FuturaLT", sans-serif;
  font-size: clamp(28px, 3vw, 42px);
  line-height: 1.1;
}

.page-description,
.section-caption {
  color: #667a78;
}

.workspace-panel {
  overflow: hidden;
  border-color: #dce5e3 !important;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(25, 49, 47, 0.05);
}

.panel-toolbar,
.registration-header,
.registration-filters {
  padding: 20px 22px;
}

.panel-toolbar,
.registration-filters {
  border-bottom: 1px solid #e6eceb;
}

.section-title {
  margin: 0;
  color: #193d39;
  font-family: "Averta", "FuturaLT", sans-serif;
  font-size: 21px;
}

.toolbar-actions,
.status-totals,
.registration-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.registration-controls {
  gap: 18px;
}

.search-field {
  width: min(340px, 46vw);
}

.voucher-table >>> tbody tr {
  cursor: pointer;
}

.voucher-table >>> tbody tr:hover {
  background: #f5faf8 !important;
}

.event-id,
.detail-label {
  color: #82918f;
  font-size: 12px;
}

.field-caption {
  color: #6d7f7d;
  font-size: 12.5px;
  line-height: 1.45;
  max-width: 640px;
}

.qr-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-preview-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  padding: 10px;
  border: 1px solid #e2eae7;
  border-radius: 12px;
  background: #f8fbfa;
}

.qr-preview-frame img {
  display: block;
  width: 100%;
  max-width: 260px;
  height: auto;
  border-radius: 8px;
}

.qr-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 18px;
  color: #82918f;
  font-size: 12.5px;
  text-align: center;
}

.qr-preview-caption {
  color: #82918f;
  font-size: 11.5px;
  line-height: 1.4;
}

.send-one-button {
  min-width: 96px;
  letter-spacing: 0.01em;
}

.already-sent-warning >>> .v-alert__content {
  line-height: 1.35;
}

.send-summary {
  border: 1px solid #e2eae7;
  border-radius: 10px;
  padding: 12px 14px;
  background: #f8fbfa;
}

.send-summary-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 5px 0;
  font-size: 13px;
  word-break: break-word;
}

.send-summary-row + .send-summary-row {
  border-top: 1px solid #edf3f1;
}

.summary-line {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.summary-total {
  margin-right: 7px;
  font-weight: 700;
}

.summary-dot {
  width: 7px;
  height: 7px;
  margin-left: 4px;
  border-radius: 50%;
}

.accepted-dot { background: #4e9a85; }
.pending-dot { background: #d9aa37; }
.cancelled-dot { background: #bf6970; }

.registration-header {
  background: linear-gradient(105deg, #f1f8f6 0%, #fbf8f2 100%);
}

.share-link {
  display: block;
  max-width: min(580px, 65vw);
  overflow: hidden;
  padding: 0;
  color: #2d766e;
  font-size: 12px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-totals {
  gap: 0;
}

.status-metric {
  min-width: 74px;
  padding: 0 14px;
  border-left: 1px solid #d6e3df;
  text-align: center;
}

.metric-value,
.metric-label {
  display: block;
}

.metric-value {
  color: #123f3a;
  font-family: "Averta", "FuturaLT", sans-serif;
  font-size: 22px;
  font-weight: 700;
}

.metric-label {
  color: #71827f;
  font-size: 10px;
  text-transform: uppercase;
}

.registration-filters {
  justify-content: flex-start;
}

.registration-search {
  margin-right: auto;
}

.delivery-state {
  margin-top: 4px;
  font-size: 10px;
  white-space: nowrap;
}

.sent-state {
  color: #2d766e;
}

.unsent-state {
  color: #8b7025;
}

.delivery-error {
  max-width: 190px;
  overflow: hidden;
  color: #a3484f;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.registrations-table >>> tbody tr {
  cursor: default;
}

.status-select {
  max-width: 145px;
  border-radius: 5px;
}

.status-select >>> .v-input__slot {
  min-height: 36px !important;
}

.status-select >>> .v-select__selection {
  color: #283f3c;
  font-size: 12px;
  font-weight: 700;
}

.code-cell {
  max-width: 210px;
  overflow-wrap: anywhere;
  color: #435b58;
  font-family: "Menlo", "Consolas", monospace;
  font-size: 11px;
}

.voucher-option {
  min-width: 0;
}

.voucher-option .v-list-item__title {
  color: #24413d;
  font-size: 13px;
  font-weight: 700;
}

.voucher-option .v-list-item__subtitle {
  overflow: hidden;
  color: #758683;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.voucher-selection-text {
  overflow: hidden;
  color: #294440;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.voucher-selection-text small {
  color: #2d766e;
  font-weight: 700;
}

.voucher-specimen {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: -10px;
  padding: 14px 16px;
  border: 1px solid #cfe1dd;
  border-radius: 9px;
  background: linear-gradient(105deg, #f0f8f6, #f8fbfa);
}

.voucher-specimen-value {
  min-width: 105px;
  color: #1f776d;
  font-family: "Averta", "FuturaLT", sans-serif;
  font-size: 25px;
  font-weight: 800;
  line-height: 1;
}

.voucher-specimen-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.voucher-specimen-copy strong {
  overflow: hidden;
  color: #294440;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.voucher-specimen-copy span {
  color: #657976;
  font-size: 11px;
}

.voucher-specimen-copy small {
  margin-top: 3px;
  color: #91a09e;
  font-family: "Menlo", "Consolas", monospace;
  font-size: 9px;
}

.empty-review {
  padding: 64px 20px;
  color: #748784;
  text-align: center;
}

.empty-review h3 {
  margin: 12px 0 4px;
  color: #38514e;
}

.voucher-dialog {
  border-radius: 12px;
}

.dialog-title {
  border-bottom: 1px solid #e5ecea;
  color: #123f3a;
  font-family: "Averta", "FuturaLT", sans-serif;
}

.dialog-actions {
  padding: 16px 24px 20px;
  border-top: 1px solid #e5ecea;
  justify-content: flex-end;
}

.template-strip,
.dispatch-preview {
  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: 10px;
}

.template-strip {
  padding: 15px;
  color: #dff5f0;
  background: #123f3a;
}

.template-icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  place-items: center;
}

.template-id {
  margin-top: 4px;
  color: #8fc5bd;
  font-family: "Menlo", "Consolas", monospace;
  font-size: 10px;
}

.field-label {
  color: #334e4b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.audience-option {
  min-height: 60px;
  margin: 0 0 8px !important;
  padding: 10px 14px;
  border: 1px solid #dfe8e6;
  border-radius: 8px;
}

.audience-option.v-item--active {
  border-color: #69a59d;
  background: #f1f8f6;
}

.audience-title {
  color: #294440;
  font-size: 13px;
  font-weight: 700;
}

.audience-description {
  margin-top: 2px;
  color: #738481;
  font-size: 11px;
}

.dispatch-preview {
  min-height: 76px;
  padding: 14px 18px;
  border: 1px solid #cde0dc;
  background: #f4faf8;
}

.dispatch-count {
  min-width: 50px;
  color: #1f776d;
  font-family: "Averta", "FuturaLT", sans-serif;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
}

.blast-failures {
  max-height: 190px;
  overflow-y: auto;
  border: 1px solid #ead5d7;
  border-radius: 8px;
}

.failure-row {
  display: grid;
  grid-template-columns: minmax(110px, 1fr) minmax(120px, 1fr);
  gap: 2px 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #f1e4e5;
  color: #4f6764;
  font-size: 11px;
}

.failure-row:last-child {
  border-bottom: 0;
}

.failure-row small {
  grid-column: 1 / -1;
  color: #a3484f;
}

@media (max-width: 900px) {
  .page-heading,
  .panel-toolbar,
  .registration-header,
  .registration-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .page-heading .v-btn {
    align-self: flex-start;
  }

  .toolbar-actions,
  .registration-filters .v-btn-toggle {
    width: 100%;
  }

  .search-field,
  .share-link {
    width: 100%;
    max-width: 100%;
  }

  .status-totals {
    align-self: stretch;
    justify-content: space-between;
  }

  .registration-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .blast-button {
    align-self: flex-end;
  }

  .status-metric {
    min-width: 0;
    flex: 1;
    padding: 0 7px;
  }

  .registration-filters .v-btn-toggle {
    overflow-x: auto;
  }

  .registration-filters .v-btn-toggle .v-btn {
    flex: 1;
  }
}

@media (max-width: 600px) {
  .voucher-admin {
    padding: 12px !important;
  }

  .page-heading {
    margin-bottom: 18px !important;
  }

  .panel-toolbar,
  .registration-header,
  .registration-filters {
    padding: 16px;
  }

  .toolbar-actions {
    align-items: stretch;
  }

  .status-metric:first-child {
    border-left: 0;
  }

  .metric-value {
    font-size: 18px;
  }

  .blast-button {
    width: 100%;
  }

  .voucher-specimen {
    align-items: flex-start;
    flex-direction: column;
    gap: 7px;
  }

  .voucher-specimen-value {
    font-size: 22px;
  }
}
</style>
